"use client";

import { FeatureList } from "../FeatureList";
import { OrderSummaryCard } from "../OrderSummaryCard";
import { RemediationPlanCard } from "../RemediationPlanCard";
import { YearChoiceList } from "../YearChoiceList";
import { REMEDIATION_OPTIONS, REMEDIATION_PLAN, type PackageSummary } from "../pricing";

/**
 * How many past financial years of company-secretary filings need doing, and
 * what that costs.
 *
 * Reached only from a "yes" on the past-returns step, and repeats that step's
 * heading — the design treats the two as one question asked in two parts.
 */
export function ManageRemediationStep({
  years,
  onYearsChange,
  summary,
  onNext,
}: {
  years: string | null;
  onYearsChange: (value: string) => void;
  summary: PackageSummary;
  onNext: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (years) onNext();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col items-start gap-6 sm:gap-8 lg:items-end">
          <h1 className="w-full font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[53px] lg:leading-[71px]">
            Need help with annual returns from previous years?
          </h1>

          <p className="w-full font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
            As your new company secretary, we can assist with filing outstanding{" "}
            <br className="max-sm:hidden" />
            annual returns to ensure compliance.
          </p>

          <YearChoiceList
            legend="How many past years need filing?"
            name="remediation-years"
            options={REMEDIATION_OPTIONS}
            value={years}
            onChange={onYearsChange}
          />

          <div className="flex w-full flex-col items-start gap-6 sm:gap-8">
            <h2 className="w-full font-suisse-works text-[28px] leading-tight text-white [text-shadow:0px_4px_24px_rgba(0,0,0,0.05)] sm:text-[40px] sm:leading-[64px] lg:text-5xl">
              Your remediation plan
            </h2>

            <RemediationPlanCard plan={REMEDIATION_PLAN} />

            <FeatureList title="What's included" items={REMEDIATION_PLAN.features} />
          </div>

          <button
            type="submit"
            disabled={!years}
            className="flex h-14 w-full items-center justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 lg:max-w-[388px]"
          >
            Next
          </button>
        </div>

        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
